import { useEffect, useState } from 'react';
import { ApiError, api, type Nutzer, type NutzerEintrag } from '../api/client';
import { Feld, Karte, Meldung, fehlerText } from '../components/ui';
import { MATERIAL_TEXT } from '../format';
import { regelwerkSpeichern, ressourcenSetzen } from '../aktionen';
import { useUndo } from '../undo';

/** Regelwerk (JSON mit Prüfung), Materialbestand und Nutzer der Organisation. */
export function Einstellungen({ nutzer }: { nutzer: Nutzer }) {
  const darf = nutzer.rolle === 'planer';
  return (
    <div className="seite">
      <h1>Einstellungen – {nutzer.organisation.name}</h1>
      <div className="seite--zweispaltig">
        <RegelwerkEditor darf={darf} />
        <div>
          <Ressourcen darf={darf} />
          <NutzerVerwaltung darf={darf} ich={nutzer} />
        </div>
      </div>
    </div>
  );
}

function RegelwerkEditor({ darf }: { darf: boolean }) {
  const [text, setText] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [meldung, setMeldung] = useState<{
    art: 'fehler' | 'erfolg';
    text: string;
    details?: string[];
  } | null>(null);

  const undo = useUndo();
  const stand = undo.stand;
  const [alt, setAlt] = useState<unknown>(null);
  useEffect(() => {
    api
      .regelwerk()
      .then((r) => {
        setText(JSON.stringify(r.regelwerk?.inhalt ?? {}, null, 2));
        setVersion(r.regelwerk?.version ?? null);
        setAlt(r.regelwerk?.inhalt ?? null);
      })
      .catch((e: unknown) => setMeldung({ art: 'fehler', text: fehlerText(e) }));
  }, [stand]);

  async function speichern() {
    let inhalt: unknown;
    try {
      inhalt = JSON.parse(text);
    } catch (e) {
      setMeldung({ art: 'fehler', text: `Kein gültiges JSON: ${fehlerText(e)}` });
      return;
    }
    try {
      await undo.tue(regelwerkSpeichern(alt, inhalt));
      const r = await api.regelwerk();
      setVersion(r.regelwerk?.version ?? null);
      setAlt(inhalt);
      setMeldung({
        art: 'erfolg',
        text: `Gespeichert als Version ${r.regelwerk?.version ?? '?'}. Gilt ab der nächsten Prüfung.`,
      });
    } catch (e) {
      setMeldung({
        art: 'fehler',
        text: fehlerText(e),
        details: e instanceof ApiError ? e.details : [],
      });
    }
  }

  return (
    <Karte titel={`Regelwerk${version ? ` (Version ${version})` : ''}`}>
      <p className="gedaempft">
        Alle Zeit- und Besetzungswerte sind Annahmen der Organisation, keine Norm. Jede Regel
        braucht eine Begründung in einem Satz. Änderungen erzeugen eine neue Version.
      </p>
      <textarea
        className="json-editor"
        spellCheck={false}
        value={text}
        readOnly={!darf}
        onChange={(e) => setText(e.target.value)}
        aria-label="Regelwerk als JSON"
      />
      {meldung && (
        <Meldung art={meldung.art}>
          {meldung.text}
          {meldung.details && meldung.details.length > 0 && (
            <ul>
              {meldung.details.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
        </Meldung>
      )}
      {darf && (
        <button type="button" className="knopf knopf--primaer" onClick={() => void speichern()}>
          Prüfen und speichern
        </button>
      )}
    </Karte>
  );
}

function Ressourcen({ darf }: { darf: boolean }) {
  const [bestand, setBestand] = useState<Record<string, number>>({});
  const [neu, setNeu] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [gespeichert, setGespeichert] = useState<Record<string, number>>({});
  const undo = useUndo();
  const stand = undo.stand;
  useEffect(() => {
    api
      .ressourcen()
      .then((r) => {
        setBestand(r.ressourcen);
        setGespeichert(r.ressourcen);
      })
      .catch((e: unknown) => setMeldung(fehlerText(e)));
  }, [stand]);
  return (
    <Karte titel="Materialbestand">
      <table className="tabelle tabelle--kompakt">
        <tbody>
          {Object.entries(bestand).map(([art, n]) => (
            <tr key={art}>
              <td>{MATERIAL_TEXT[art] ?? art}</td>
              <td>
                <input
                  type="number"
                  min={0}
                  value={n}
                  disabled={!darf}
                  aria-label={`Bestand ${MATERIAL_TEXT[art] ?? art}`}
                  onChange={(e) =>
                    setBestand({ ...bestand, [art]: Math.max(0, Number(e.target.value)) })
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {darf && (
        <>
          <Feld
            label="Weitere Materialart"
            hinweis="Schlüssel wie im Regelwerk, z. B. absperrgitter"
          >
            <input
              value={neu}
              onChange={(e) => setNeu(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            />
          </Feld>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              disabled={!neu}
              onClick={() => {
                setBestand({ ...bestand, [neu]: 0 });
                setNeu('');
              }}
            >
              Hinzufügen
            </button>
            <button
              type="button"
              className="knopf knopf--primaer"
              onClick={() =>
                void undo
                  .tue(ressourcenSetzen(gespeichert, bestand))
                  .then(() => {
                    setGespeichert(bestand);
                    setMeldung('Gespeichert.');
                  })
                  .catch((e: unknown) => setMeldung(fehlerText(e)))
              }
            >
              Bestand speichern
            </button>
          </div>
        </>
      )}
      {meldung && <p>{meldung}</p>}
    </Karte>
  );
}

function NutzerVerwaltung({ darf, ich }: { darf: boolean; ich: Nutzer }) {
  const [liste, setListe] = useState<NutzerEintrag[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [rolle, setRolle] = useState('leser');
  const [passwort, setPasswort] = useState('');
  useEffect(() => {
    api
      .nutzer()
      .then((r) => setListe(r.nutzer))
      .catch((e: unknown) => setFehler(fehlerText(e)));
  }, []);
  const aendern = (id: string, d: Parameters<typeof api.nutzerAendern>[1]) =>
    void api
      .nutzerAendern(id, d)
      .then((r) => setListe(r.nutzer))
      .catch((e: unknown) => setFehler(fehlerText(e)));

  return (
    <Karte titel="Nutzer und Rollen">
      <table className="tabelle tabelle--kompakt">
        <tbody>
          {liste.map((n) => (
            <tr key={n.id}>
              <td>
                {n.name}
                <div className="gedaempft">{n.email}</div>
              </td>
              <td>
                <select
                  value={n.rolle}
                  disabled={!darf || n.id === ich.id}
                  onChange={(e) => aendern(n.id, { rolle: e.target.value })}
                  aria-label={`Rolle ${n.name}`}
                >
                  <option value="planer">Planer</option>
                  <option value="leser">Leser</option>
                </select>
              </td>
              <td>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={n.aktiv}
                    disabled={!darf || n.id === ich.id}
                    onChange={(e) => aendern(n.id, { aktiv: e.target.checked })}
                  />{' '}
                  aktiv
                </label>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      {darf && (
        <details>
          <summary>Person hinzufügen</summary>
          <form
            className="formular"
            onSubmit={(e) => {
              e.preventDefault();
              void api
                .nutzerAnlegen({ name, email, rolle, passwort })
                .then((r) => {
                  setListe(r.nutzer);
                  setName('');
                  setEmail('');
                  setPasswort('');
                })
                .catch((x: unknown) => setFehler(fehlerText(x)));
            }}
          >
            <Feld label="Name">
              <input value={name} onChange={(e) => setName(e.target.value)} required />
            </Feld>
            <Feld label="E-Mail">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Feld>
            <Feld label="Rolle">
              <select value={rolle} onChange={(e) => setRolle(e.target.value)}>
                <option value="leser">Leser (nur ansehen)</option>
                <option value="planer">Planer (bearbeiten)</option>
              </select>
            </Feld>
            <Feld
              label="Erstes Passwort"
              hinweis="Mindestens 10 Zeichen; der Person auf sicherem Weg mitteilen."
            >
              <input
                type="password"
                autoComplete="new-password"
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
                required
                minLength={10}
              />
            </Feld>
            <button type="submit" className="knopf">
              Anlegen
            </button>
          </form>
        </details>
      )}
    </Karte>
  );
}
